import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AgentFloatManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [floatHistory, setFloatHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  // Float operation form
  const [showFloatModal, setShowFloatModal] = useState(false);
  const [floatForm, setFloatForm] = useState({
    agentId: "",
    amount: "",
    type: "Allocate", // Allocate or Recall
    description: "",
    reference: ""
  });
  const [processing, setProcessing] = useState(false);

  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load agents with float info using the Accounting endpoint
      const res = await api.get("/Accounting/agent-floats");
      const agentsList = res.data || [];
      
      // Map the data to our format - KEEP agentUserId for API calls
      const agentsWithFloat = agentsList.map(agent => ({
        id: agent.agentUserId || agent.id,
        agentUserId: agent.agentUserId, // Keep original for API calls
        fullName: agent.agentName || agent.fullName,
        email: agent.email || "",
        accountNumber: agent.accountNumber,
        floatBalance: agent.balance || agent.walletBalance || 0,
        isActive: agent.status === "Active" || agent.isActive,
        status: agent.status || (agent.isActive ? "Active" : "Inactive")
      }));

      setAgents(agentsWithFloat);

      // Calculate stats
      const totalFloat = agentsWithFloat.reduce((sum, a) => sum + (a.floatBalance || 0), 0);
      const activeAgents = agentsWithFloat.filter(a => a.isActive || a.status === "Active").length;
      
      setStats({
        totalAgents: agentsWithFloat.length,
        activeAgents,
        totalFloatAllocated: totalFloat,
        averageFloat: agentsWithFloat.length > 0 ? totalFloat / agentsWithFloat.length : 0
      });

    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load agent data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadFloatHistory = async (agentId) => {
    try {
      const res = await api.get(`/Transaction/agent-float-history/${agentId}?limit=20`);
      setFloatHistory(res.data || []);
    } catch (err) {
      console.error("Error loading float history:", err);
      // Try alternative endpoint
      try {
        const agent = agents.find(a => a.id === agentId);
        if (agent?.accountNumber) {
          const res = await api.get(`/Transaction/all?accountNumber=${agent.accountNumber}&pageSize=20`);
          setFloatHistory(res.data || []);
        }
      } catch {
        setFloatHistory([]);
      }
    }
  };

  const handleSelectAgent = (agent) => {
    setSelectedAgent(agent);
    loadFloatHistory(agent.id);
  };

  const handleFloatOperation = async (e) => {
    e.preventDefault();
    if (!floatForm.agentId || !floatForm.amount) {
      alert("Please select an agent and enter an amount");
      return;
    }

    setProcessing(true);
    try {
      const agent = agents.find(a => a.id === parseInt(floatForm.agentId));
      const agentUserId = agent.agentUserId || agent.id; // Use agentUserId for API
      
      let successMessage = "";
      
      if (floatForm.type === "Allocate") {
        // Load float to agent using Accounting endpoint
        const response = await api.post(`/Accounting/agent-floats/${agentUserId}/load`, {
          amount: parseFloat(floatForm.amount),
          description: floatForm.description || `Float allocation - ${floatForm.reference || 'Admin'}`
        });
        successMessage = `✅ Successfully allocated ₦${parseFloat(floatForm.amount).toLocaleString()} to ${agent.fullName}\nNew Balance: ₦${response.data.newBalance?.toLocaleString() || 'N/A'}`;
      } else {
        // Withdraw float from agent using Accounting endpoint
        const response = await api.post(`/Accounting/agent-floats/${agentUserId}/withdraw`, {
          amount: parseFloat(floatForm.amount),
          description: floatForm.description || `Float recall - ${floatForm.reference || 'Admin'}`
        });
        successMessage = `✅ Successfully recalled ₦${parseFloat(floatForm.amount).toLocaleString()} from ${agent.fullName}\nNew Balance: ₦${response.data.newBalance?.toLocaleString() || 'N/A'}`;
      }

      // Close modal and reset form FIRST
      setShowFloatModal(false);
      setFloatForm({ agentId: "", amount: "", type: "Allocate", description: "", reference: "" });
      
      // Reload all data to get updated balances
      const res = await api.get("/Accounting/agent-floats");
      const agentsList = res.data || [];
      
      const agentsWithFloat = agentsList.map(a => ({
        id: a.agentUserId || a.id,
        agentUserId: a.agentUserId,
        fullName: a.agentName || a.fullName,
        email: a.email || "",
        accountNumber: a.accountNumber,
        floatBalance: a.balance || a.walletBalance || 0,
        isActive: a.status === "Active" || a.isActive,
        status: a.status || (a.isActive ? "Active" : "Inactive")
      }));

      // Update stats
      const totalFloat = agentsWithFloat.reduce((sum, a) => sum + (a.floatBalance || 0), 0);
      const activeAgents = agentsWithFloat.filter(a => a.isActive || a.status === "Active").length;
      
      // Update all state at once
      setAgents(agentsWithFloat);
      setStats({
        totalAgents: agentsWithFloat.length,
        activeAgents,
        totalFloatAllocated: totalFloat,
        averageFloat: agentsWithFloat.length > 0 ? totalFloat / agentsWithFloat.length : 0
      });

      // Update selected agent if one was selected
      if (selectedAgent) {
        const updatedAgent = agentsWithFloat.find(a => a.id === selectedAgent.id);
        if (updatedAgent) {
          setSelectedAgent(updatedAgent);
          loadFloatHistory(updatedAgent.id);
        }
      }
      
      // Show success message AFTER all state updates
      alert(successMessage);
      
    } catch (err) {
      console.error("Float operation failed:", err);
      alert(err.response?.data?.message || "Float operation failed");
    } finally {
      setProcessing(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.accountNumber?.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && agent.isActive) ||
                         (statusFilter === "inactive" && !agent.isActive);
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading Agent Float Management...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
          <h1 style={styles.title}>💵 Agent Float Management</h1>
        </div>
        <button onClick={() => setShowFloatModal(true)} style={styles.primaryBtn}>
          + Allocate/Recall Float
        </button>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>👥</div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{stats.totalAgents}</span>
              <span style={styles.statLabel}>Total Agents</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: "#d1fae5" }}>✅</div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{stats.activeAgents}</span>
              <span style={styles.statLabel}>Active Agents</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: "#dbeafe" }}>💰</div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{formatCurrency(stats.totalFloatAllocated)}</span>
              <span style={styles.statLabel}>Total Float Allocated</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: "#fef3c7" }}>📊</div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{formatCurrency(stats.averageFloat)}</span>
              <span style={styles.statLabel}>Avg Float per Agent</span>
            </div>
          </div>
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Agent List */}
        <div style={styles.agentListSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Agents</h2>
            <div style={styles.filters}>
              <input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={styles.agentList}>
            {filteredAgents.length === 0 ? (
              <div style={styles.emptyState}>No agents found</div>
            ) : (
              filteredAgents.map(agent => (
                <div
                  key={agent.id}
                  style={{
                    ...styles.agentCard,
                    borderColor: selectedAgent?.id === agent.id ? "#3b82f6" : "#e5e7eb",
                    backgroundColor: selectedAgent?.id === agent.id ? "#eff6ff" : "white"
                  }}
                  onClick={() => handleSelectAgent(agent)}
                >
                  <div style={styles.agentHeader}>
                    <div style={styles.agentAvatar}>
                      {agent.fullName?.charAt(0).toUpperCase()}
                    </div>
                    <div style={styles.agentInfo}>
                      <span style={styles.agentName}>{agent.fullName}</span>
                      <span style={styles.agentEmail}>{agent.email}</span>
                      <span style={styles.agentAccount}>Acc: {agent.accountNumber || "N/A"}</span>
                    </div>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: agent.isActive ? "#d1fae5" : "#fee2e2",
                      color: agent.isActive ? "#059669" : "#dc2626"
                    }}>
                      {agent.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={styles.agentFloat}>
                    <span style={styles.floatLabel}>Current Float</span>
                    <span style={styles.floatValue}>{formatCurrency(agent.floatBalance)}</span>
                  </div>
                  <div style={styles.agentActions}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFloatForm({ ...floatForm, agentId: agent.id.toString(), type: "Allocate" });
                        setShowFloatModal(true);
                      }}
                      style={styles.allocateBtn}
                    >
                      + Allocate
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFloatForm({ ...floatForm, agentId: agent.id.toString(), type: "Recall" });
                        setShowFloatModal(true);
                      }}
                      style={styles.recallBtn}
                    >
                      - Recall
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Float History / Details */}
        <div style={styles.detailsSection}>
          {selectedAgent ? (
            <>
              <div style={styles.selectedAgentHeader}>
                <h2 style={styles.sectionTitle}>
                  {selectedAgent.fullName}'s Float History
                </h2>
                <div style={styles.selectedAgentFloat}>
                  Current Balance: <strong>{formatCurrency(selectedAgent.floatBalance)}</strong>
                </div>
              </div>

              <div style={styles.historyList}>
                {floatHistory.length === 0 ? (
                  <div style={styles.emptyState}>No float history available</div>
                ) : (
                  floatHistory.map((tx, idx) => (
                    <div key={idx} style={styles.historyItem}>
                      <div style={styles.historyLeft}>
                        <span style={{
                          ...styles.historyIcon,
                          backgroundColor: tx.type === "Credit" || tx.type === "Deposit" ? "#d1fae5" : "#fee2e2",
                          color: tx.type === "Credit" || tx.type === "Deposit" ? "#059669" : "#dc2626"
                        }}>
                          {tx.type === "Credit" || tx.type === "Deposit" ? "↓" : "↑"}
                        </span>
                        <div>
                          <div style={styles.historyDesc}>{tx.description || tx.type}</div>
                          <div style={styles.historyDate}>{formatDate(tx.createdAt || tx.date)}</div>
                        </div>
                      </div>
                      <span style={{
                        ...styles.historyAmount,
                        color: tx.type === "Credit" || tx.type === "Deposit" ? "#059669" : "#dc2626"
                      }}>
                        {tx.type === "Credit" || tx.type === "Deposit" ? "+" : "-"}
                        {formatCurrency(Math.abs(tx.amount))}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div style={styles.noSelection}>
              <span style={styles.noSelectionIcon}>👈</span>
              <p>Select an agent to view their float history</p>
            </div>
          )}
        </div>
      </div>

      {/* Float Operation Modal */}
      {showFloatModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {floatForm.type === "Allocate" ? "💵 Allocate Float" : "📤 Recall Float"}
              </h2>
              <button onClick={() => setShowFloatModal(false)} style={styles.closeBtn}>×</button>
            </div>

            <form onSubmit={handleFloatOperation}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Agent *</label>
                <select
                  value={floatForm.agentId}
                  onChange={(e) => setFloatForm({ ...floatForm, agentId: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Agent --</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.fullName} ({agent.accountNumber}) - Balance: {formatCurrency(agent.floatBalance)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Operation Type *</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="type"
                      value="Allocate"
                      checked={floatForm.type === "Allocate"}
                      onChange={(e) => setFloatForm({ ...floatForm, type: e.target.value })}
                    />
                    <span style={styles.radioText}>➕ Allocate (Add Float)</span>
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="type"
                      value="Recall"
                      checked={floatForm.type === "Recall"}
                      onChange={(e) => setFloatForm({ ...floatForm, type: e.target.value })}
                    />
                    <span style={styles.radioText}>➖ Recall (Remove Float)</span>
                  </label>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₦) *</label>
                <input
                  type="number"
                  value={floatForm.amount}
                  onChange={(e) => setFloatForm({ ...floatForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  style={styles.input}
                  min="1"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Reference</label>
                <input
                  type="text"
                  value={floatForm.reference}
                  onChange={(e) => setFloatForm({ ...floatForm, reference: e.target.value })}
                  placeholder="e.g., FLT-2024-001"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={floatForm.description}
                  onChange={(e) => setFloatForm({ ...floatForm, description: e.target.value })}
                  placeholder="Add notes about this float operation..."
                  style={styles.textarea}
                  rows={3}
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowFloatModal(false)}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  style={{
                    ...styles.submitBtn,
                    backgroundColor: floatForm.type === "Allocate" ? "#059669" : "#dc2626",
                    opacity: processing ? 0.7 : 1
                  }}
                >
                  {processing ? "Processing..." : (floatForm.type === "Allocate" ? "Allocate Float" : "Recall Float")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f3f4f6",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: "20px"
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #e5e7eb",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  header: {
    backgroundColor: "#1e3a5f",
    color: "white",
    padding: "20px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px"
  },
  backBtn: {
    padding: "8px 16px",
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    color: "white",
    borderRadius: "6px",
    cursor: "pointer"
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "600"
  },
  primaryBtn: {
    padding: "12px 24px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    padding: "20px 30px"
  },
  statCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
  },
  statIcon: {
    width: "50px",
    height: "50px",
    backgroundColor: "#f3f4f6",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px"
  },
  statInfo: {
    display: "flex",
    flexDirection: "column"
  },
  statValue: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1f2937"
  },
  statLabel: {
    fontSize: "12px",
    color: "#6b7280"
  },
  errorBox: {
    margin: "0 30px",
    padding: "15px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    borderRadius: "8px"
  },
  mainContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    padding: "0 30px 30px"
  },
  agentListSection: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
  },
  sectionHeader: {
    marginBottom: "20px"
  },
  sectionTitle: {
    margin: "0 0 15px",
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937"
  },
  filters: {
    display: "flex",
    gap: "10px"
  },
  searchInput: {
    flex: 1,
    padding: "10px 15px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px"
  },
  filterSelect: {
    padding: "10px 15px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "white"
  },
  agentList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxHeight: "500px",
    overflowY: "auto"
  },
  agentCard: {
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    padding: "15px",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  agentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px"
  },
  agentAvatar: {
    width: "45px",
    height: "45px",
    backgroundColor: "#3b82f6",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "600"
  },
  agentInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  agentName: {
    fontWeight: "600",
    color: "#1f2937"
  },
  agentEmail: {
    fontSize: "12px",
    color: "#6b7280"
  },
  agentAccount: {
    fontSize: "11px",
    color: "#9ca3af",
    fontFamily: "monospace"
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600"
  },
  agentFloat: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0",
    borderTop: "1px solid #f3f4f6",
    borderBottom: "1px solid #f3f4f6",
    marginBottom: "12px"
  },
  floatLabel: {
    fontSize: "12px",
    color: "#6b7280"
  },
  floatValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#059669"
  },
  agentActions: {
    display: "flex",
    gap: "8px"
  },
  allocateBtn: {
    flex: 1,
    padding: "8px",
    backgroundColor: "#d1fae5",
    color: "#059669",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer"
  },
  recallBtn: {
    flex: 1,
    padding: "8px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer"
  },
  detailsSection: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
  },
  selectedAgentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "1px solid #e5e7eb"
  },
  selectedAgentFloat: {
    fontSize: "14px",
    color: "#6b7280"
  },
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "450px",
    overflowY: "auto"
  },
  historyItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#f9fafb",
    borderRadius: "8px"
  },
  historyLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  historyIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "bold"
  },
  historyDesc: {
    fontWeight: "500",
    fontSize: "14px",
    color: "#1f2937"
  },
  historyDate: {
    fontSize: "12px",
    color: "#9ca3af"
  },
  historyAmount: {
    fontWeight: "600",
    fontSize: "15px"
  },
  noSelection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "300px",
    color: "#9ca3af"
  },
  noSelectionIcon: {
    fontSize: "48px",
    marginBottom: "10px"
  },
  emptyState: {
    textAlign: "center",
    padding: "40px",
    color: "#9ca3af"
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    width: "500px",
    maxWidth: "90%",
    maxHeight: "90vh",
    overflow: "auto"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e5e7eb"
  },
  modalTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "600"
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
    color: "#6b7280"
  },
  formGroup: {
    padding: "0 20px",
    marginBottom: "20px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151"
  },
  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "white"
  },
  textarea: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    resize: "vertical",
    boxSizing: "border-box"
  },
  radioGroup: {
    display: "flex",
    gap: "20px"
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },
  radioText: {
    fontSize: "14px"
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    padding: "20px",
    borderTop: "1px solid #e5e7eb"
  },
  cancelBtn: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#f3f4f6",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  submitBtn: {
    flex: 1,
    padding: "12px",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  }
};
